import { useState, useEffect, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useDepartment } from '../../context/DepartmentContext'
import { useToast } from '../../context/ToastContext'
import { getCategories, UNIT_LABELS, UNIT_OPTIONS } from '../../data/categories'
import { getProducts, findProductByCode } from '../../data/products'
import {
  saveMorningLog, saveSurplusEntry, saveSaleTransaction, getMorningLogs,
  getSurplus, getSalesTransactions, saveFlagsBatch,
  deleteMorningLogBatch, deleteSurplusBatch, deleteSalesForDate, deleteSaleTransaction,
  setVorsWithSteak, setPapWarmers, getPapWarmers, fetchPapWarmers,
} from '../../lib/store'
import { generateTransactionFlags } from '../../lib/flagging'
import { calculateExpectedSales, formatR } from '../../lib/calculations'
import { Save, Copy, Plus, Trash2, AlertTriangle, Download, Eye, Edit3, Search } from 'lucide-react'
import AddProductModal from './AddProductModal'
import AddCategoryModal from './AddCategoryModal'
import ArithmeticInput from '../ui/ArithmeticInput'
import ArithmeticTip from '../ui/ArithmeticTip'

// Categories that support optional piece count alongside rand value
const PIECE_COUNT_CATEGORIES = ['steak', 'vors', 'stew vors', 'magwanda']

// Check if a value has been explicitly entered (including zero)
function isExplicitlyEntered(val) {
  return val !== undefined && val !== '' && val !== null
}

// ─── Morning Log Form ───
function MorningLogForm({ department, date, onSaved, steakMode = 'vors' }) {
  const { addToast } = useToast()
  const [values, setValues] = useState({})
  const [units, setUnits] = useState({})
  const [pieceCounts, setPieceCounts] = useState({})
  const [viewMode, setViewMode] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [catKey, setCatKey] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  // Pap warmers — standalone field on the meta doc, not a category. Stored
  // as a string so the input can render '' (empty) until the user types.
  // Validated on save: must be explicitly entered (0 is valid).
  const [papWarmers, setPapWarmersState] = useState('')

  const categories = getCategories()
  // Morning log no longer collects 'pap only' — staff stopped tracking it
  // here. The 'pap only' category still exists for downstream PAP
  // reconciliation (driven by Full/Half/Plate meal sales), but the morning
  // log opening is just left at 0 and the reconciliation row will show
  // '—' rather than firing a shortfall flag.
  // Vors row is also hidden in any steak mode that doesn't serve vors
  // (i.e. 'no_vors' or 'extra_steak'). Only the 'vors' mode shows it.
  const visibleCategories = categories.filter(c => {
    if (c.name === 'pap only') return false
    if (c.name === 'vors' && steakMode !== 'vors') return false
    return true
  })
  const filteredCategories = searchTerm
    ? visibleCategories.filter(c => c.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : visibleCategories

  // When the steak mode switches away from 'vors', force any existing
  // vors values to zero so the record either saves as 0 or is harmlessly
  // skipped by the save loop. Both 'no_vors' and 'extra_steak' clear them.
  useEffect(() => {
    if (steakMode === 'vors') return
    setValues(prev => (prev.vors === 0 && (!prev.vors || prev.vors === '') ? prev : { ...prev, vors: 0 }))
    setPieceCounts(prev => (prev.vors === 0 || prev.vors === undefined ? prev : { ...prev, vors: 0 }))
  }, [steakMode])

  // Auto-load data when date changes
  const loadData = useCallback(() => {
    const logs = getMorningLogs(department, date)
    if (logs.length > 0) {
      const v = {}, u = {}, pc = {}
      for (const l of logs) {
        v[l.category] = l.value
        if (l.unit) u[l.category] = l.unit
        if (l.openingPieces) pc[l.category] = l.openingPieces
      }
      setValues(v)
      setUnits(u)
      setPieceCounts(pc)
      setViewMode(true)
    } else {
      setValues({})
      setUnits({})
      setPieceCounts({})
      setViewMode(false)
    }
    // Load papWarmers from the meta doc cache (sync). On a fresh date with
    // no entry yet, getPapWarmers returns null and the field renders ''.
    const cachedWarmers = getPapWarmers(department, date)
    setPapWarmersState(cachedWarmers != null ? String(cachedWarmers) : '')
  }, [department, date])

  useEffect(() => { loadData() }, [loadData])

  // Cross-device fallback: if the cache is empty, fetch the meta doc from
  // Firestore once and update the field. fetchPapWarmers also opportunistically
  // hydrates the vorsWithSteak cache from the same fetch result.
  useEffect(() => {
    let cancelled = false
    if (getPapWarmers(department, date) == null) {
      fetchPapWarmers(department, date).then(v => {
        if (!cancelled && v != null) setPapWarmersState(String(v))
      }).catch(err => {
        console.warn('[ManualEntry] fetchPapWarmers failed:', err?.message)
        Sentry.captureException(err, { extra: { fn: 'ManualEntry.fetchPapWarmers', department, date } })
      })
    }
    return () => { cancelled = true }
  }, [department, date])

  const loadYesterday = () => {
    const yesterday = new Date(date)
    yesterday.setDate(yesterday.getDate() - 1)
    const yStr = yesterday.toISOString().split('T')[0]
    const logs = getMorningLogs(department, yStr)
    if (logs.length === 0) { addToast('No data from previous day', 'info'); return }
    const v = {}, u = {}, pc = {}
    for (const l of logs) {
      v[l.category] = l.value
      if (l.unit) u[l.category] = l.unit
      if (l.openingPieces) pc[l.category] = l.openingPieces
    }
    setValues(v)
    setUnits(u)
    setPieceCounts(pc)
    addToast(`Loaded values from ${yStr}`, 'info')
  }

  const save = () => {
    // Validate: all required fields must be explicitly entered (including 0)
    const cats = getCategories()
    const missing = []
    for (const cat of cats) {
      // PAP is no longer collected in the morning log — skip it entirely.
      if (cat.name === 'pap only') continue
      // When vors is hidden for the day (no_vors or extra_steak), skip
      // its validation entirely.
      if (cat.name === 'vors' && steakMode !== 'vors') continue

      const val = values[cat.name]
      if (!isExplicitlyEntered(val)) {
        missing.push(cat.label)
      }

      // Steak and vors require pieces whenever rands > 0.
      // (pieces = 0 or blank is only valid when rands = 0.)
      if ((cat.name === 'steak' || cat.name === 'vors') && Number(val) > 0) {
        const pcs = Number(pieceCounts[cat.name])
        if (!pcs || pcs <= 0) {
          missing.push(
            cat.name === 'steak'
              ? 'Steak pieces required — enter number of pieces purchased'
              : 'Vors pieces required — enter number of pieces purchased'
          )
        }
      }
    }

    // Pap warmers — always required, 0 is valid, decimals allowed (8.5 = half warmer).
    // Tracked alongside the existing morning log fields so a single Save
    // click validates and persists everything together.
    if (!isExplicitlyEntered(papWarmers)) {
      missing.push('Pap warmers required — enter 0 if no pap was made today')
    } else if (!Number.isFinite(Number(papWarmers)) || Number(papWarmers) < 0) {
      missing.push('Pap warmers must be a non-negative number (e.g. 8 or 8.5)')
    }

    if (missing.length > 0) {
      setValidationErrors(missing)
      return
    }
    setValidationErrors([])

    let count = 0
    for (const cat of cats) {
      // PAP is no longer collected in the morning log — skip writing
      // any 'pap only' record. Downstream PAP reconciliation reads from
      // sales, not from a morning-log opening pool.
      if (cat.name === 'pap only') continue
      // Skip writing a vors record entirely in any mode that doesn't
      // serve vors (no_vors or extra_steak).
      if (cat.name === 'vors' && steakMode !== 'vors') continue

      const val = values[cat.name]
      const unit = units[cat.name] || cat.openingUnit

      if (isExplicitlyEntered(val)) {
        const pc = PIECE_COUNT_CATEGORIES.includes(cat.name) ? (Number(pieceCounts[cat.name]) || null) : null
        saveMorningLog(department, date, cat.name, val, unit, 'manual', '', pc)
        count++
      }
    }

    // Persist the steak-service mode alongside the morning log so the
    // setting reaches Firestore as part of the morning log save flow.
    setVorsWithSteak(department, date, steakMode)

    // Persist the pap warmers count to the same meta doc. setPapWarmers
    // uses the merge-safe _composeMetaDoc helper so it does not clobber
    // the vorsWithSteak field that setVorsWithSteak just wrote.
    setPapWarmers(department, date, Number(papWarmers))

    addToast(`Saved ${count} morning log entries`, 'success')
    setViewMode(true)
    onSaved?.()
  }

  const handleDelete = () => {
    if (!confirm(`Delete all morning log entries for ${date}?`)) return
    const count = deleteMorningLogBatch(department, date)
    addToast(`Deleted ${count} morning log entries`, 'success')
    setValues({})
    setUnits({})
    setPieceCounts({})
    setViewMode(false)
    onSaved?.()
  }

  const hasData = Object.values(values).some(v => v !== undefined && v !== '' && v !== null)

  // ─── View Mode ───
  if (viewMode && hasData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Morning Log
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(false)}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
            >
              <Edit3 size={12} /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        <div className="space-y-1">
          {visibleCategories.map(cat => {
            const val = values[cat.name]
            if (val === undefined || val === '' || val === null) return null
            const unit = units[cat.name] || cat.openingUnit
            return (
              <div key={cat.name} className="flex items-center justify-between bg-gray-800/50 rounded px-4 py-2">
                <span className="text-sm capitalize">{cat.label}</span>
                <div className="text-right">
                  <span className="text-sm font-medium text-white">
                    {`${val} ${UNIT_LABELS[unit]}`}
                  </span>
                  {pieceCounts[cat.name] > 0 && (
                    <span className="text-[10px] text-gray-500 ml-2">({pieceCounts[cat.name]} pcs)</span>
                  )}
                </div>
              </div>
            )
          })}
          {/* Show pap warmers in the saved view alongside the categories */}
          {papWarmers !== '' && papWarmers !== null && (
            <div className="flex items-center justify-between bg-gray-800/50 rounded px-4 py-2">
              <span className="text-sm">Pap warmers</span>
              <span className="text-sm font-medium text-white">{papWarmers}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ─── Edit Mode ───
  return (
    <div className="space-y-4">
      <ArithmeticTip />
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-gray-900 border border-gray-700 rounded pl-9 pr-3 py-1.5 text-sm text-white placeholder:text-gray-500"
          />
        </div>
        <button
          onClick={loadYesterday}
          className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-2 py-1.5 rounded text-xs"
        >
          <Copy size={12} /> Copy from prev day
        </button>
        <button
          onClick={() => setShowAddCategory(true)}
          className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-amber-400 px-3 py-2 rounded text-sm"
        >
          <Plus size={14} /> Add Meat Type
        </button>
      </div>

      <div className="space-y-1">
        {filteredCategories.map(cat => {
          // (PAP morning log entry has been removed — staff no longer
          // track Full/Half/Plate openings here. The 'pap only' category
          // is filtered out of visibleCategories above so this loop never
          // sees it.)

          // Standard categories — single numeric input
          return (
            <div key={cat.name} className="bg-gray-800/50 rounded px-4 py-1.5">
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm capitalize">{cat.label}</span>
                <div className="flex items-center gap-2">
                  <select
                    value={units[cat.name] || cat.openingUnit}
                    onChange={e => setUnits(prev => ({ ...prev, [cat.name]: e.target.value }))}
                    className="bg-gray-900 border border-gray-700 rounded px-1 py-1 text-xs text-gray-400 w-20"
                  >
                    {UNIT_OPTIONS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                  <ArithmeticInput
                    min="0"
                    value={values[cat.name] ?? ''}
                    onChange={(val) => setValues(prev => ({ ...prev, [cat.name]: val == null ? '' : val }))}
                    className="bg-gray-900 border border-gray-700 rounded px-3 py-1 text-sm text-white w-28"
                    placeholder="0"
                  />
                </div>
              </div>
              {PIECE_COUNT_CATEGORIES.includes(cat.name) && (
                <div className="flex items-center gap-2 mt-1 ml-32 pl-4">
                  <ArithmeticInput
                    min="0"
                    value={pieceCounts[cat.name] ?? ''}
                    onChange={(val) => setPieceCounts(prev => ({ ...prev, [cat.name]: val == null ? '' : val }))}
                    className="bg-gray-900/50 border border-gray-700/50 rounded px-2 py-0.5 text-xs text-gray-300 w-20"
                    placeholder="pieces"
                  />
                  <span className="text-[10px] text-gray-600">
                    pieces ({(cat.name === 'steak' || cat.name === 'vors') ? 'required' : 'optional'})
                  </span>
                  {cat.name !== 'steak' && cat.name !== 'vors' &&
                    Number(pieceCounts[cat.name]) > 0 && Number(values[cat.name]) > 0 && (
                    <span className="text-[10px] text-gray-500">
                      ≈ R{(Number(values[cat.name]) / Number(pieceCounts[cat.name])).toFixed(2)}/pc
                    </span>
                  )}
                </div>
              )}
              {(cat.name === 'steak' || cat.name === 'vors') &&
                Number(values[cat.name]) > 0 && Number(pieceCounts[cat.name]) > 0 && (
                <div className="ml-32 pl-4 mt-0.5">
                  <span className="text-[10px] text-gray-500">
                    Unit price: R{(Number(values[cat.name]) / Number(pieceCounts[cat.name])).toFixed(2)}/piece
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Pap warmers — standalone field saved on the meta doc, not a
          category. Always required so analytics can compute average
          revenue per warmer over time. 0 is valid (no pap made today). */}
      <div className="bg-gray-800/50 rounded px-4 py-3">
        <label className="block">
          <span className="text-sm text-gray-200 font-medium">Pap warmers prepared this morning</span>
          <span className="block text-[10px] text-gray-500 mt-0.5">
            How many warmers of pap were made? Decimals allowed (e.g. 8.5 for a half warmer). Enter 0 if none.
          </span>
          <ArithmeticInput
            min="0"
            step="0.5"
            value={papWarmers}
            onChange={(val) => setPapWarmersState(val == null ? '' : String(val))}
            className="mt-2 bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white w-32"
            placeholder="0"
          />
        </label>
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-950/40 border border-red-800/50 rounded-lg px-4 py-3">
          <p className="text-sm text-red-300 font-medium mb-1">Please enter opening stock for:</p>
          <p className="text-xs text-red-400">{validationErrors.join(', ')}</p>
          <p className="text-[10px] text-red-500 mt-1">Enter 0 for any category with no stock today.</p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
        >
          <Save size={16} /> Save Morning Log
        </button>
        <button
          onClick={() => {
            const v = { ...values }
            for (const cat of visibleCategories) {
              if (!isExplicitlyEntered(v[cat.name])) {
                v[cat.name] = 0
              }
            }
            setValues(v)
            setValidationErrors([])
            addToast('Empty fields set to zero', 'info')
          }}
          className="text-xs text-gray-500 hover:text-gray-300 px-3 py-2 rounded border border-gray-700 hover:border-gray-600 transition-colors"
        >
          Fill empty with 0
        </button>
      </div>

      {showAddCategory && (
        <AddCategoryModal
          onClose={() => setShowAddCategory(false)}
          onCategoryAdded={() => setCatKey(k => k + 1)}
        />
      )}
    </div>
  )
}

// Categories that support optional surplus piece count
const SURPLUS_PIECE_CATEGORIES = ['steak', 'vors', 'stew vors', 'magwanda']

// ─── Surplus Form ───
function SurplusForm({ department, date, onSaved }) {
  const { addToast } = useToast()
  const [values, setValues] = useState({})
  const [units, setUnits] = useState({})
  const [surplusPieceCounts, setSurplusPieceCounts] = useState({})
  const [viewMode, setViewMode] = useState(false)
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [catKey, setCatKey] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  const categories = getCategories()
  const filteredCategories = searchTerm
    ? categories.filter(c => c.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : categories

  const loadData = useCallback(() => {
    const logs = getSurplus(department, date)
    if (logs.length > 0) {
      const v = {}, u = {}, sp = {}
      for (const l of logs) {
        v[l.category] = l.value
        if (l.unit) u[l.category] = l.unit
        if (l.surplusPieces) sp[l.category] = l.surplusPieces
      }
      setValues(v)
      setUnits(u)
      setSurplusPieceCounts(sp)
      setViewMode(true)
    } else {
      setValues({})
      setUnits({})
      setSurplusPieceCounts({})
      setViewMode(false)
    }
  }, [department, date])

  useEffect(() => { loadData() }, [loadData])

  const getMorningVal = (catName) => {
    const logs = getMorningLogs(department, date)
    return logs.find(l => l.category === catName)?.value
  }

  const getMorningUnit = (catName) => {
    const logs = getMorningLogs(department, date)
    return logs.find(l => l.category === catName)?.unit
  }

  const save = () => {
    let count = 0
    const cats = getCategories()

    for (const cat of cats) {
      const val = values[cat.name]
      const unit = units[cat.name] || cat.surplusUnit
      if (val !== undefined && val !== '' && val !== null) {
        const sp = SURPLUS_PIECE_CATEGORIES.includes(cat.name) ? (Number(surplusPieceCounts[cat.name]) || null) : null
        saveSurplusEntry(department, date, cat.name, val, unit, 'manual', sp)
        count++
      }
    }

    addToast(`Saved ${count} entries`, 'success')
    setViewMode(true)
    onSaved?.()
  }

  const handleDelete = () => {
    if (!confirm(`Delete all surplus entries for ${date}?`)) return
    const count = deleteSurplusBatch(department, date)
    addToast(`Deleted ${count} surplus entries`, 'success')
    setValues({})
    setUnits({})
    setSurplusPieceCounts({})
    setViewMode(false)
    onSaved?.()
  }

  const hasData = Object.values(values).some(v => v !== undefined && v !== '' && v !== null)

  // ─── View Mode ───
  if (viewMode && hasData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Surplus
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(false)}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
            >
              <Edit3 size={12} /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        <div className="space-y-1">
          {categories.map(cat => {
            const val = values[cat.name]
            if (val === undefined || val === '' || val === null) return null
            const unit = units[cat.name] || cat.surplusUnit
            const morningVal = getMorningVal(cat.name)
            const morningUnit = getMorningUnit(cat.name)
            return (
              <div key={cat.name} className="flex items-center justify-between bg-gray-800/50 rounded px-4 py-2">
                <span className="text-sm capitalize">{cat.label}</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-500">
                    Opening: {morningVal !== undefined ? `${morningVal} ${UNIT_LABELS[morningUnit || cat.openingUnit]}` : '\u2014'}
                  </span>
                  <div className="text-right">
                    <span className="text-sm font-medium text-white">
                      {val} {UNIT_LABELS[unit]}
                    </span>
                    {surplusPieceCounts[cat.name] > 0 && (
                      <span className="text-[10px] text-gray-500 ml-2">({surplusPieceCounts[cat.name]} pcs)</span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ─── Edit Mode ───
  return (
    <div className="space-y-4">
      <ArithmeticTip />
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-gray-900 border border-gray-700 rounded pl-9 pr-3 py-1.5 text-sm text-white placeholder:text-gray-500"
          />
        </div>
        <button
          onClick={() => setShowAddCategory(true)}
          className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-amber-400 px-3 py-2 rounded text-sm"
        >
          <Plus size={14} /> Add Meat Type
        </button>
      </div>

      <div className="space-y-1">
        {filteredCategories.map(cat => {
          const morningVal = getMorningVal(cat.name)
          const morningUnit = getMorningUnit(cat.name)
          const surplusVal = Number(values[cat.name]) || 0
          const warn = morningVal !== undefined && surplusVal > morningVal
          return (
            <div key={cat.name} className={`rounded px-4 py-1.5 ${warn ? 'bg-amber-950/30 border border-amber-800' : 'bg-gray-800/50'}`}>
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm capitalize">{cat.label}</span>
                <span className="text-gray-500 text-xs w-28">
                  Opening: {morningVal !== undefined ? `${morningVal} ${UNIT_LABELS[morningUnit || cat.openingUnit]}` : '\u2014'}
                </span>
                <div className="flex items-center gap-2">
                  <select
                    value={units[cat.name] || cat.surplusUnit}
                    onChange={e => setUnits(prev => ({ ...prev, [cat.name]: e.target.value }))}
                    className="bg-gray-900 border border-gray-700 rounded px-1 py-1 text-xs text-gray-400 w-20"
                  >
                    {UNIT_OPTIONS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                  <ArithmeticInput
                    min="0"
                    value={values[cat.name] ?? ''}
                    onChange={(val) => setValues(prev => ({ ...prev, [cat.name]: val == null ? '' : val }))}
                    className="bg-gray-900 border border-gray-700 rounded px-3 py-1 text-sm text-white w-28"
                    placeholder="0"
                  />
                </div>
                {warn && <AlertTriangle size={14} className="text-amber-400" />}
              </div>
              {SURPLUS_PIECE_CATEGORIES.includes(cat.name) && (
                <div className="flex items-center gap-2 mt-1 ml-32 pl-4">
                  <ArithmeticInput
                    min="0"
                    value={surplusPieceCounts[cat.name] ?? ''}
                    onChange={(val) => setSurplusPieceCounts(prev => ({ ...prev, [cat.name]: val == null ? '' : val }))}
                    className="bg-gray-900/50 border border-gray-700/50 rounded px-2 py-0.5 text-xs text-gray-300 w-20"
                    placeholder="pieces"
                  />
                  <span className="text-[10px] text-gray-600">surplus pieces (optional)</span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
        >
          <Save size={16} /> Save Surplus
        </button>
        <button
          onClick={() => {
            const cats = getCategories()
            const v = {}
            for (const cat of cats) v[cat.name] = 0
            setValues(v)
            setSurplusPieceCounts({})
            addToast('All surplus set to zero — all sold', 'info')
          }}
          className="text-xs text-gray-500 hover:text-gray-300 px-3 py-2 rounded border border-gray-700 hover:border-gray-600 transition-colors"
        >
          No surplus — all sold
        </button>
      </div>

      {showAddCategory && (
        <AddCategoryModal
          onClose={() => setShowAddCategory(false)}
          onCategoryAdded={() => setCatKey(k => k + 1)}
        />
      )}
    </div>
  )
}

// ─── Sales Form ───
function SalesForm({ department, date, onSaved }) {
  const { addToast } = useToast()
  const [rows, setRows] = useState([])
  const [viewMode, setViewMode] = useState(false)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [addProductForRowId, setAddProductForRowId] = useState(null)
  const [productListKey, setProductListKey] = useState(0)

  const loadData = useCallback(() => {
    const txns = getSalesTransactions(department, date)
    if (txns.length > 0) {
      setRows(txns.map(t => ({
        id: t.id || Date.now() + Math.random(),
        date: t.date || date,
        productCode: t.productCode || '',
        productName: t.productName || '',
        category: t.category || '',
        unitPrice: t.unitPrice || null,
        qps: t.qps || null,
        saleType: t.saleType || '',
        qtySold: t.qtySold || '',
        salesRands: t.salesRands || '',
      })))
      setViewMode(true)
    } else {
      setRows([])
      setViewMode(false)
    }
  }, [department, date])

  useEffect(() => { loadData() }, [loadData])

  // Re-read from localStorage when background sync updates data
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.department === department && e.detail?.date === date) loadData()
    }
    window.addEventListener('desres-sync', handler)
    return () => window.removeEventListener('desres-sync', handler)
  }, [department, date, loadData])

  const addRow = () => {
    setRows(prev => [...prev, {
      id: Date.now(),
      date,
      productCode: '',
      productName: '',
      category: '',
      unitPrice: null,
      qps: null,
      saleType: '',
      qtySold: '',
      salesRands: '',
    }])
  }

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r
      const updated = { ...r, [field]: value }
      if (field === 'productCode') {
        const product = findProductByCode(value, department)
        if (product) {
          updated.productName = product.name
          updated.category = product.category
          updated.unitPrice = product.unitPrice
          updated.qps = product.qps
          updated.saleType = product.saleType
        }
      }
      return updated
    }))
  }

  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id))

  const save = async () => {
    const allFlags = []
    let count = 0

    // Clear all sales for this date then re-save everything shown in the form
    await deleteSalesForDate(department, date)

    for (const row of rows) {
      if (!row.productCode) continue
      if (row.salesRands === '' || row.salesRands === undefined || row.salesRands === null) continue
      const tx = {
        date,
        productCode: row.productCode,
        productName: row.productName,
        category: row.category,
        qtySold: Number(row.qtySold) || 0,
        salesRands: Number(row.salesRands) || 0,
        unitPrice: row.unitPrice,
        qps: row.qps,
        saleType: row.saleType,
        totalSalesExpected: calculateExpectedSales(row.unitPrice, Number(row.qtySold)),
        difference: row.unitPrice && row.qtySold
          ? Number(row.salesRands) - (row.unitPrice * Number(row.qtySold))
          : null,
        entryMode: 'manual',
      }
      const result = saveSaleTransaction(department, tx)
      const flags = generateTransactionFlags({ ...tx, id: result.record.id })
      allFlags.push(...flags)
      count++
    }

    if (allFlags.length > 0) saveFlagsBatch(department, allFlags)

    addToast(
      `Saved ${count} sales, ${allFlags.length} flags raised`,
      count > 0 ? 'success' : 'warning'
    )
    setViewMode(true)
    loadData()
    onSaved?.()
  }

  const handleDelete = async () => {
    if (!confirm(`Delete all sales entries for ${date}?`)) return
    const count = await deleteSalesForDate(department, date)
    addToast(`Deleted ${count} sales entries`, 'success')
    setRows([])
    setViewMode(false)
    onSaved?.()
  }

  const hasData = rows.length > 0

  // ─── View Mode ───
  if (viewMode && hasData) {
    const totalSales = rows.reduce((s, r) => s + (Number(r.salesRands) || 0), 0)
    const totalQty = rows.reduce((s, r) => s + (Number(r.qtySold) || 0), 0)
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Eye size={16} /> Saved Sales — {rows.length} items, {formatR(totalSales)} total
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(false)}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
            >
              <Edit3 size={12} /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
            >
              <Trash2 size={12} /> Delete All
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Product</th>
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-right py-2 px-2">Qty</th>
                <th className="text-right py-2 px-2">Sales (R)</th>
                <th className="text-right py-2 px-2">Expected</th>
                <th className="text-right py-2 px-2">Diff</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const expected = calculateExpectedSales(row.unitPrice, Number(row.qtySold))
                const diff = expected && row.salesRands ? Number(row.salesRands) - expected : null
                return (
                  <tr key={row.id} className={`border-b border-gray-800/50 ${diff && diff !== 0 ? 'bg-amber-950/20' : ''}`}>
                    <td className="py-2 px-2 font-mono text-xs text-gray-400">{row.productCode}</td>
                    <td className="py-2 px-2 text-sm">{row.productName || '\u2014'}</td>
                    <td className="py-2 px-2 text-xs text-gray-400 capitalize">{row.category || '\u2014'}</td>
                    <td className="py-2 px-2 text-right">{row.qtySold || '\u2014'}</td>
                    <td className="py-2 px-2 text-right font-medium">{formatR(row.salesRands)}</td>
                    <td className="py-2 px-2 text-right text-xs text-gray-400">{expected !== null ? formatR(expected) : '\u2014'}</td>
                    <td className={`py-2 px-2 text-right text-xs font-medium ${
                      diff > 0 ? 'text-green-400' : diff < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {diff !== null ? `${diff >= 0 ? '+' : ''}${formatR(diff)}` : '\u2014'}
                    </td>
                    <td className="py-2 px-2">
                      <button
                        onClick={() => {
                          if (!confirm(`Delete ${row.productName || row.productCode}?`)) return
                          deleteSaleTransaction(row.id)
                          loadData()
                          addToast('Sale entry deleted', 'success')
                          onSaved?.()
                        }}
                        className="text-gray-600 hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-700 text-sm font-medium">
                <td colSpan="3" className="py-2 px-2 text-gray-400">Total</td>
                <td className="py-2 px-2 text-right">{totalQty}</td>
                <td className="py-2 px-2 text-right text-white">{formatR(totalSales)}</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    )
  }

  // ─── Edit Mode ───
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={addRow}
          className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm"
        >
          <Plus size={14} /> Add Sale
        </button>
      </div>

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Product</th>
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-right py-2 px-2">Qty</th>
                <th className="text-right py-2 px-2">Sales (R)</th>
                <th className="text-right py-2 px-2">Expected</th>
                <th className="text-right py-2 px-2">Diff</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const expected = calculateExpectedSales(row.unitPrice, Number(row.qtySold))
                const diff = expected && row.salesRands ? Number(row.salesRands) - expected : null
                return (
                  <tr key={row.id} className={`border-b border-gray-800/50 ${diff && diff !== 0 ? 'bg-amber-950/20' : ''}`}>
                    <td className="py-2 px-2">
                      <select
                        value={row.productCode}
                        onChange={e => {
                          if (e.target.value === '__NEW__') {
                            setAddProductForRowId(row.id)
                            setShowAddProduct(true)
                          } else {
                            updateRow(row.id, 'productCode', e.target.value)
                          }
                        }}
                        className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white w-28"
                      >
                        <option value="">Select</option>
                        {getProducts(department).map(p => (
                          <option key={p.code} value={p.code}>{p.code}</option>
                        ))}
                        <option value="__NEW__">+ New Item</option>
                      </select>
                    </td>
                    <td className="py-2 px-2 text-gray-400 text-xs">{row.productName || '\u2014'}</td>
                    <td className="py-2 px-2 text-gray-400 text-xs capitalize">{row.category || '\u2014'}</td>
                    <td className="py-2 px-2">
                      <ArithmeticInput
                        min="0"
                        value={row.qtySold}
                        onChange={(val) => updateRow(row.id, 'qtySold', val == null ? '' : val)}
                        className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white w-20 text-right"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <ArithmeticInput
                        min="0"
                        value={row.salesRands}
                        onChange={(val) => updateRow(row.id, 'salesRands', val == null ? '' : val)}
                        className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white w-24 text-right"
                      />
                    </td>
                    <td className="py-2 px-2 text-right text-xs text-gray-400">{expected !== null ? formatR(expected) : '\u2014'}</td>
                    <td className={`py-2 px-2 text-right text-xs font-medium ${
                      diff > 0 ? 'text-green-400' : diff < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {diff !== null ? `${diff >= 0 ? '+' : ''}${formatR(diff)}` : '\u2014'}
                    </td>
                    <td className="py-2 px-2">
                      <button onClick={() => removeRow(row.id)} className="text-gray-600 hover:text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <button
          onClick={save}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
        >
          <Save size={16} /> Save Sales
        </button>
      )}

      {rows.length === 0 && (
        <div className="text-center py-12 text-gray-600 text-sm">
          Click "Add Sale" to start entering sales data
        </div>
      )}

      {showAddProduct && (
        <AddProductModal
          department={department}
          onClose={() => { setShowAddProduct(false); setAddProductForRowId(null) }}
          onProductAdded={(product) => {
            setProductListKey(k => k + 1)
            if (addProductForRowId) {
              updateRow(addProductForRowId, 'productCode', product.code)
            }
          }}
        />
      )}
    </div>
  )
}

export default function ManualEntry({ dataType, date, onSaved, steakMode = 'vors' }) {
  const { currentDepartmentId } = useDepartment()

  if (dataType === 'morning') return <MorningLogForm department={currentDepartmentId} date={date} onSaved={onSaved} steakMode={steakMode} />
  if (dataType === 'surplus') return <SurplusForm department={currentDepartmentId} date={date} onSaved={onSaved} />
  if (dataType === 'sales') return <SalesForm department={currentDepartmentId} date={date} onSaved={onSaved} />
  return null
}
